import { UploadOutlined } from "@ant-design/icons"
import { Button, Col, Divider, Flex, Form, Image, Row, Select, Skeleton, Space, Typography, Upload, message } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useEffect, useState } from "react"

import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { validateFile } from "../../../../../utils/file-validation.ts"
import {
  getBlockImages,
  uploadBlockImage
} from "../../../redux/thunks/document-templates.thunks.ts"
import type { TemplateDocumentFormData } from "../../../types/template-document-form-data.tsx"
import type { DocumentTemplateFile } from "../../../types/types.ts"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

const MAX_SIZE_MB = 10
const ALLOWED_FILE_EXTENSIONS = ["png", "jpeg", "jpg"]

const keys: [
  keyof TemplateDocumentFormData,
  keyof TemplateDocumentFormData["block_data"]
] = ["block_data", "image_id"]

interface ImageBlockProps {
  fieldKey: string
  form: FormInstance<TemplateDocumentFormData>
}

export function ImageBlock({ fieldKey, form }: ImageBlockProps) {
  const dispatch = useAppDispatch()
  const [images, setImages] = useState<DocumentTemplateFile[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  const path = [keys[0], fieldKey, keys[1]]
  const hasInitialValue = form.getFieldValue(path)
  const selectedImageId = Form.useWatch(path, form)

  const loadImages = async () => {
    setLoading(true)
    try {
      const result = await dispatch(getBlockImages()).unwrap()
      setImages(result || [])
      setHasLoaded(true)
    } catch (error) {
      message.error("Failed to load images")
    } finally {
      setLoading(false)
    }
  }

  const handleDropdownVisibleChange = (open: boolean) => {
    if (open && !hasLoaded) {
      void loadImages()
    }
  }

  const handleUpload = async (file: File) => {
    try {
      setUploading(true)
      const result = await dispatch(uploadBlockImage(file)).unwrap()

      if (result?.id) {
        setImages((prev) => [...prev, result])

        form.setFieldsValue({
          [keys[0]]: { [fieldKey]: { [keys[1]]: result.id } }
        })
      }
    } catch (error) {
      message.error("Failed to upload image")
    } finally {
      setUploading(false)
    }
  }

  const handleBeforeUpload = (file: File) => {
    const result = validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS,
      defaultResult: true
    })

    if (result === true) {
      void handleUpload(file)
    }

    return false
  }

  const selectedImage = images.find((img) => img.id === selectedImageId)

  useEffect(() => {
    if (hasInitialValue) void loadImages()
  }, [])

  return (
    <>
      {loading && hasInitialValue ? (
        <Skeleton.Image active className={"size-56"} />
      ) : selectedImage ? (
        <Image
          src={selectedImage?.file_url}
          alt="Preview"
          loading="lazy"
          width={224}
          height={224}
          className={"max-w-56 max-h-56 object-contain"}
          placeholder={<Skeleton.Node active className={"size-56"} />}
        />
      ) : null}

      <Form.Item label="Image">
        <Space.Compact className={"w-full gap-2"}>
          <Form.Item
            name={path}
            className={"w-full mb-0"}
            rules={[
              { required: false, message: "Please select or upload an image" }
            ]}
          >
            <Select
              allowClear
              className={"flex-1"}
              placeholder="Select an existing image"
              loading={loading}
              disabled={uploading || (loading && !!hasInitialValue)}
              onOpenChange={handleDropdownVisibleChange}
              options={images.map((img) => ({
                label:
                  img?.file_name ||
                  `Image ${new Date(img?.created).toLocaleDateString()}`,
                value: img.id
              }))}
            />
          </Form.Item>
          <Upload
            multiple={false}
            beforeUpload={handleBeforeUpload}
            accept={ALLOWED_FILE_EXTENSIONS?.map((ext) => `.${ext}`).join(",")}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              Upload
            </Button>
          </Upload>
        </Space.Compact>
      </Form.Item>

      <Row gutter={8}>
        <Col sm={24} md={20}>
          <Form.Item
            name={[keys[0], fieldKey, "caption"]}
            label="Caption"
            rules={[{ max: 255, message: "Maximum 255 characters" }]}
          >
            <TextInput placeholder="Optional caption" />
          </Form.Item>
        </Col>

        <Col sm={24} md={4}>
          <Form.Item
            name={[keys[0], fieldKey, "width"]}
            label="Width"
            initialValue="full"
          >
            <Select
              options={[
                { label: "Full", value: "full" },
                { label: "Large", value: "large" },
                { label: "Medium", value: "medium" },
                { label: "Small", value: "small" }
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider size={"small"} />

      <Flex vertical>
        <Typography.Text type={"secondary"} className={"text-xs text-end"}>
          {ALLOWED_FILE_EXTENSIONS?.join(", ")?.toUpperCase()} • Up to{" "}
          {MAX_SIZE_MB}MB
        </Typography.Text>
      </Flex>
    </>
  )
}
